/**
 * Alpine host for the location prompts that the server lists in `pending_prompts` (docs#10).
 *
 * The server renders the first prompt kind the web knows into
 * layouts/partials/location-prompt-host.twig. This component shows it at most once per browser
 * tab session. Each action posts the prompt_id of the rendered prompt, so the server refuses an
 * action on a prompt that a newer one replaced.
 */

import apiClient from '../api-client.js';
import { SESSION_STORAGE, safeStorageAvailable, safeStorageGet, safeStorageRemove, safeStorageSet } from '../safe-storage.js';
import { withDeadline } from '../timeout-signal.js';

export const SESSION_KEY = 'locationPrompt:shown';

/** A request with no response in this time fails, so the card does not stay disabled. */
const REQUEST_TIMEOUT_MS = 15000;

/** Returned by an action that reloads the page, so the card stays busy until the reload. */
const RELOADING = 'reloading';

/** A response body that does not arrive in this time counts as no body, so the prompt stops being busy. */
const BODY_TIMEOUT_MS = 5000;

/** Marks a toast notice the member left. For the rest of the session, the next page shows any other prompt, but not that notice. */
const LEFT_PREFIX = 'left:';

/** True when this tab has not shown a location prompt yet, or only left a different notice. Claims the slot. */
export function claimSessionSlot(promptId = '') {
    // Without sessionStorage the host cannot keep the once-per-session rule, so it shows nothing.
    if (!safeStorageAvailable(SESSION_STORAGE)) {
        return false;
    }
    const held = safeStorageGet(SESSION_KEY, SESSION_STORAGE);
    if (held !== null && !(held.startsWith(LEFT_PREFIX) && held !== LEFT_PREFIX + promptId)) {
        return false;
    }
    return safeStorageSet(SESSION_KEY, '1', SESSION_STORAGE);
}

/** The parsed JSON body of a response, or null when it is not JSON or does not arrive in time. */
function jsonBody(response) {
    return withDeadline(() => response.json(), BODY_TIMEOUT_MS).catch(() => null);
}

/** An API request that rejects with an AbortError after REQUEST_TIMEOUT_MS without a response. */
async function boundedRequest(path, options) {
    return withDeadline((signal) => apiClient.request(path, { ...options, signal }), REQUEST_TIMEOUT_MS);
}

/** The `error.code` of an API error body, or null when the body is not one or does not arrive in time. */
async function errorCode(response) {
    return (await jsonBody(response))?.error?.code ?? null;
}

/** The automatic-change notice that a dismiss response returned, or null. */
export function returnedAutoNotice(body) {
    return (body?.pending_prompts ?? []).find((prompt) => prompt?.kind === 'city_auto_notice') ?? null;
}

/** Window of the notice toast. Keep in step with the default of the mobile ActionToast. */
const NOTICE_TOAST_MS = 6000;

/** Fills one %token% with a value. A function replacement keeps `$` in a city name literal. */
function fill(template, token, value) {
    return (template ?? '').replaceAll(token, () => value ?? '');
}

/** Frees this tab's prompt slot when a dismiss body lists another prompt, so the next page load can show it. */
function releaseSlotForPending(body) {
    if ((body?.pending_prompts ?? []).length > 0) {
        safeStorageRemove(SESSION_KEY, SESSION_STORAGE);
    }
}

/** A `toast` style renders the automatic-change notice as an action toast in place of the card. */
export function locationPromptHost(kind, promptId = '', userId = '', style = 'sheet') {
    return {
        kind,
        promptId,
        userId: String(userId),
        style,
        visible: false,
        busy: false,
        // The action that runs, for its spinner.
        pending: '',
        closing: false,
        expired: false,
        // An age contact hold refused the action. A hold lifts, unlike a 409.
        held: false,
        // After an Accept tap the tap is the member's answer, so a close sends no dismiss.
        answered: false,
        state: 'ask',
        error: '',
        staysTitle: '',
        askAgain: '',
        staysToast: '',
        noticeTitle: '',
        noticeBody: '',
        // True while the notice shows as a toast, so an Undo result has a place to go with the card hidden.
        toastMode: false,
        // The new city of the notice, for the failed-Undo message.
        noticeCity: '',
        // The element of the last notice toast, which keeps the focus through its fade after a close.
        toastElement: null,

        init() {
            const claimed = claimSessionSlot(this.promptId);
            // Without the toast helper the notice shows as its card. A live action toast of another
            // feature also gets the card, because a second action toast would close its Undo.
            if (claimed && this.kind === 'city_auto_notice' && this.style === 'toast'
                && typeof window.showActionToast === 'function' && !window.hasActionToast?.()) {
                this.noticeCity = this.dataset().noticeCity ?? '';
                this.showToast(this.dataset().toastMessage ?? '');
                return;
            }
            this.visible = claimed;
        },

        dataset() {
            return this.$el?.dataset ?? {};
        },

        /**
         * The window starts at the first interaction with the page, because a timeout counts as Done.
         * After a failed Undo, only an Undo tap answers the notice: any other close leaves the card.
         */
        showToast(message, afterFailure = false, takeFocus = afterFailure) {
            this.toastMode = true;
            let toast = null;
            const stopLeaveDismiss = afterFailure ? null : this.dismissOnLeave(() => toast?.dismiss('left'));
            toast = window.showActionToast(message, {
                actionLabel: this.dataset().undoLabel,
                duration: NOTICE_TOAST_MS,
                holdWhileHidden: true,
                holdUntilInteraction: !afterFailure,
                // The failed Undo button took the keyboard focus with it.
                focusAction: takeFocus,
                closeOnEscape: true,
                onAction: () => this.undo(),
                onClose: (acted, reason) => {
                    stopLeaveDismiss?.();
                    // Leaving answered the notice, so a page restored from the cache shows no dead Undo.
                    if (acted || reason === 'left') {
                        return;
                    }
                    // Another toast took the place. The card keeps the notice and its Undo on the page.
                    if (reason === 'replaced' || afterFailure) {
                        const hadFocus = Boolean(toast?.element?.contains(document.activeElement));
                        this.toastMode = false;
                        this.visible = true;
                        // The toast leaves with the focus inside it, so the card takes it.
                        if (hadFocus) {
                            this.$nextTick(() => (this.expired ? this.$refs.close : this.$refs.undo)?.focus());
                        }
                        return;
                    }
                    this.dismiss();
                },
            });
            this.toastElement = toast?.element ?? null;
        },

        /** The dismiss body names the city that stays and the wait, so the copy matches the server. */
        applyStays(body) {
            const dataset = this.dataset();
            const city = body?.current_city?.name ?? dataset.currentCity ?? '';
            this.staysTitle = fill(dataset.staysTemplate, '%city%', city);
            this.askAgain = dataset[`askAgainDays${body?.next_ask_days}`] ?? '';
            this.staysToast = fill(dataset[`staysToastDays${body?.next_ask_days}`], '%city%', city);
        },

        /** Shows a notice that a dismiss response returned, in place of the answered nudge. */
        showReturnedNotice(notice) {
            const dataset = this.dataset();
            this.kind = 'city_auto_notice';
            this.promptId = notice.payload?.prompt_id ?? '';
            this.noticeCity = notice.payload?.new_city?.name ?? '';
            this.noticeTitle = fill(dataset.noticeTitleTemplate, '%city%', this.noticeCity);
            this.noticeBody = fill(dataset.noticeBodyTemplate, '%old_city%', notice.payload?.old_city?.name);
            this.answered = false;
            this.expired = false;
            this.held = false;
            this.error = '';
            // Always the card: the member is at the card, and an action toast would close a live
            // Undo toast of another feature.
            this.state = 'notice';
            this.visible = true;
            this.$nextTick?.(() => this.$refs?.noticeTitle?.focus());
        },

        body(extra = {}) {
            const body = new URLSearchParams();
            if (this.promptId) {
                body.append('prompt_id', this.promptId);
            }
            for (const [key, value] of Object.entries(extra)) {
                body.append(key, value);
            }
            return body;
        },

        dismissRequest(options = {}) {
            return boundedRequest(`/location-prompts/${encodeURIComponent(this.kind)}/dismiss`, {
                method: 'POST',
                body: this.body(),
                ...options,
            });
        },

        /**
         * A page left while the toast is on screen answers it as a timeout does, because the window
         * starts at the first input and a link click unloads the page first. Returns the cleanup.
         */
        dismissOnLeave(closeToast) {
            let seen = !document.hidden;
            const onVisibility = () => {
                seen = seen || !document.hidden;
            };
            const onPageHide = () => {
                if (!seen || this.closing) {
                    return;
                }
                this.closing = true;
                this.dismissRequest({ keepalive: true }).catch(() => {});
                safeStorageSet(SESSION_KEY, LEFT_PREFIX + this.promptId, SESSION_STORAGE);
                closeToast();
            };
            document.addEventListener('visibilitychange', onVisibility);
            window.addEventListener('pagehide', onPageHide);
            return () => {
                document.removeEventListener('visibilitychange', onVisibility);
                window.removeEventListener('pagehide', onPageHide);
            };
        },

        /** Answers the prompt once. It has its own flag, so a toast that closes during an action request still answers. */
        async dismiss() {
            if (this.closing) {
                return;
            }
            this.closing = true;
            this.visible = false;
            // A toast answer can race a link click, so it outlives the page as the leave answer does.
            const options = this.toastMode ? { keepalive: true } : {};
            this.toastMode = false;
            try {
                const response = await this.dismissRequest(options);
                if (!response.ok) {
                    return;
                }
                const body = await jsonBody(response);
                const notice = returnedAutoNotice(body);
                if (this.kind === 'city_nudge' && body?.snoozed) {
                    this.applyStays(body);
                    // A plain toast: an action toast would close a live Undo toast of another feature.
                    window.showInfo?.(this.staysToast, { duration: NOTICE_TOAST_MS });
                    releaseSlotForPending(body);
                } else if (this.kind === 'city_nudge' && notice) {
                    this.showReturnedNotice(notice);
                } else {
                    releaseSlotForPending(body);
                }
            } catch {
                // The prompt stays pending on the server and can show again in a later session.
            } finally {
                this.closing = false;
            }
        },

        /**
         * The cross. Disabled while a request runs. It only hides the prompt after an Accept tap and
         * past the question, so it never answers twice.
         */
        close() {
            if (this.busy) {
                return;
            }
            // A held Undo keeps the notice, so its Undo can work in a later session.
            if (this.answered || this.held || this.state === 'stays' || this.state === 'off') {
                this.visible = false;
                return;
            }
            this.dismiss();
        },

        done() {
            this.visible = false;
        },

        /**
         * Runs one card action. `run` returns false after it set its own error, and a throw shows the
         * generic error. A failure gives the focus back, or shows the retry toast in toast mode.
         */
        async runAction(pending, run) {
            if (this.busy) {
                return;
            }
            const trigger = document.activeElement;
            this.busy = true;
            this.pending = pending;
            this.error = '';
            let outcome;
            try {
                outcome = await run();
            } catch {
                this.error = this.dataset().errorMessage ?? '';
                outcome = false;
            }
            if (outcome === RELOADING) {
                return;
            }
            this.busy = false;
            this.pending = '';
            if (outcome !== false) {
                return;
            }
            if (this.toastMode) {
                this.showFailedToastUndo();
                return;
            }
            this.$nextTick(() => this.restoreFocus(trigger));
        },

        /** "Not now" keeps the card open, so the member sees that the city stays. */
        notNow() {
            return this.runAction('notNow', async () => {
                const response = await this.dismissRequest();
                if (!response.ok) {
                    throw new Error('Dismiss failed');
                }
                const body = await jsonBody(response);
                const notice = returnedAutoNotice(body);
                if (body?.snoozed) {
                    this.applyStays(body);
                    this.state = 'stays';
                    this.$nextTick(() => this.$refs.staysTitle?.focus());
                    releaseSlotForPending(body);
                } else if (notice) {
                    this.showReturnedNotice(notice);
                } else {
                    this.visible = false;
                    releaseSlotForPending(body);
                }
            });
        },

        turnOffAuto() {
            return this.runAction('turnOff', async () => {
                const response = await boundedRequest(`/settings/${encodeURIComponent(this.userId)}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ auto_city: 'off' }),
                });
                if (!response.ok) {
                    throw new Error('Settings update failed');
                }
                this.state = 'off';
                this.$nextTick(() => this.$refs.offTitle?.focus());
            });
        },

        accept() {
            if (this.busy) {
                return undefined;
            }
            this.answered = true;
            return this.act('city_nudge/accept', {});
        },

        undo() {
            return this.act('city_auto_notice/undo', {});
        },

        /** Reloads on success, so the page shows the new city. */
        act(path, extra) {
            const dataset = this.dataset();
            return this.runAction('', async () => {
                const response = await boundedRequest(`/location-prompts/${path}`, {
                    method: 'POST',
                    body: this.body(extra),
                });
                if (!this.visible && !this.toastMode) {
                    // The prompt closed while the request ran.
                    return undefined;
                }
                if (response.ok) {
                    window.location.reload();
                    return RELOADING;
                }
                // `expired` hides the actions after a 409 or an age contact hold, because neither can
                // succeed in this session. Every other failure stays retryable.
                const held = response.status === 403 && await errorCode(response) === 'age_contact_hold';
                this.expired = response.status === 409 || held;
                this.held = held;
                if (held) {
                    this.error = dataset.holdMessage ?? '';
                } else if (response.status === 409) {
                    this.error = dataset.expiredMessage ?? '';
                } else {
                    this.error = dataset.errorMessage ?? '';
                }
                return false;
            });
        },

        /** A failed toast Undo shows the error in a toast. A retryable failure keeps the Undo action. */
        showFailedToastUndo() {
            // The closed toast keeps its focused button through the fade, so focus there counts as lost.
            // Focus the member moved stays where it is, so a key press there cannot run Undo.
            const active = document.activeElement;
            const lost = !active || active === document.body || Boolean(this.toastElement?.contains(active));
            if (this.expired) {
                this.toastMode = false;
                // The card shows the hold, and its Close keeps the notice.
                if (this.held) {
                    this.visible = true;
                    if (lost) {
                        this.$nextTick(() => this.$refs.close?.focus());
                    }
                    return;
                }
                window.showError?.(this.error);
                // The card answers an expired Undo on Close, so the toast answers it here.
                this.dismiss();
                return;
            }
            // A new action toast closes another feature's live Undo toast, so the card takes the retry.
            if (window.hasActionToast?.()) {
                this.toastMode = false;
                this.visible = true;
                if (lost) {
                    this.$nextTick(() => this.$refs.undo?.focus());
                }
                return;
            }
            this.showToast(fill(this.dataset().undoFailedTemplate, '%city%', this.noticeCity) || this.error, true, lost);
        },

        /**
         * A disabled or hidden action loses its focus, at a time that differs by browser. An expired
         * prompt has no action left, so the focus goes to Close, which aria-describedby links to the message.
         */
        restoreFocus(trigger) {
            if (!this.visible) {
                return;
            }
            const active = document.activeElement;
            const lost = !active || active === document.body || active === trigger
                || (this.$el.contains(active) && (active.disabled || active.offsetParent === null));
            if (lost) {
                (this.expired ? this.$refs.close : trigger)?.focus();
            }
        },
    };
}
